// Phase COTTON-RM-002 Part C — Cotton QC Pending Report
//
// Six tabs: Moisture Pending · HVI Pending · QC Hold · Approval Pending ·
// Overdue · Rejected/Return. Tab badge = backend total count.

import { useMemo, useState } from 'react'
import {
  Badge, Button, Card, Empty, InputNumber, Select, Skeleton, Space, Table,
  Tabs, Tag, Typography,
} from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import apiClient from '@/shared/api/client'
import {
  cottonRmApiPartC, type QcPendingGrnRow,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

type TabKey =
  'moisture_pending' | 'hvi_pending' | 'qc_hold' | 'approval_pending'
  | 'overdue' | 'rejected_return'

const TAB_DEFS: { key: TabKey; label: string }[] = [
  { key: 'moisture_pending', label: 'Moisture Pending' },
  { key: 'hvi_pending',      label: 'HVI Pending' },
  { key: 'qc_hold',          label: 'QC Hold' },
  { key: 'approval_pending', label: 'Approval Pending' },
  { key: 'overdue',          label: 'Overdue' },
  { key: 'rejected_return',  label: 'Rejected / Return' },
]

function csvDownload(rows: QcPendingGrnRow[], name: string) {
  if (rows.length === 0) return
  const cols = Object.keys(rows[0])
  const header = cols.join(',')
  const body = rows.map(r =>
    cols.map(c => JSON.stringify(
      (r as unknown as Record<string, unknown>)[c] ?? '',
    )).join(',')).join('\n')
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `${name}.csv`
  a.click()
  URL.revokeObjectURL(a.href)
}


function GrnTable({ rows, tab }: { rows: QcPendingGrnRow[]; tab: TabKey }) {
  const cols = useMemo(() => [
    { title: 'GRN', dataIndex: 'grn_no',
      render: (v: string, r: QcPendingGrnRow) =>
        <Link to={`/grn/${r.grn_id}`}>{v}</Link> },
    { title: 'Date', dataIndex: 'grn_date',
      render: (v: string | null) =>
        v ? new Date(v).toLocaleDateString() : '' },
    { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
    { title: 'Bales', dataIndex: 'total_bales' },
    {
      title: 'Moisture',
      render: (_: unknown, r: QcPendingGrnRow) => (
        <Space size={4}>
          <Tag color={r.moisture_pending > 0 ? 'warning' : 'success'}>
            {r.moisture_completed}/{r.moisture_required}
          </Tag>
          {r.moisture_pct_avg !== null && (
            <Text type="secondary">avg {r.moisture_pct_avg.toFixed(2)}%</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'HVI',
      render: (_: unknown, r: QcPendingGrnRow) => (
        <Tag color={r.hvi_pending > 0 ? 'warning' : 'success'}>
          {r.hvi_completed}/{r.hvi_required}
        </Tag>
      ),
    },
    {
      title: 'Status counts',
      render: (_: unknown, r: QcPendingGrnRow) => (
        <Space size={4} wrap>
          {r.hold_count > 0 && <Tag color="error">HOLD {r.hold_count}</Tag>}
          {r.rejected_count > 0 && <Tag color="red">REJ {r.rejected_count}</Tag>}
          {r.pending_count > 0 && <Tag color="warning">PEND {r.pending_count}</Tag>}
          {r.approved_count > 0 && <Tag color="success">APPR {r.approved_count}</Tag>}
        </Space>
      ),
    },
    { title: 'Age',
      render: (_: unknown, r: QcPendingGrnRow) => r.age_days !== null
        ? <Tag color={r.age_days >= 3 ? 'warning' : 'default'}>{r.age_days}d</Tag>
        : '' },
  ], [])
  if (rows.length === 0) return <Empty description="Nothing in this bucket." />
  return (
    <Table
      rowKey="grn_id" size="small" columns={cols as never}
      dataSource={rows} pagination={{ pageSize: 50 }}
      title={() => (
        <Space>
          <Text>{rows.length} GRNs</Text>
          <Button size="small" icon={<DownloadOutlined />}
                  onClick={() => csvDownload(rows, `qc-${tab}`)}>
            Export CSV
          </Button>
        </Space>
      )}
    />
  )
}


export default function CottonQcPendingPage() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [overdue, setOverdue] = useState(3)
  const [tab, setTab] = useState<TabKey>('moisture_pending')

  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-qc-pending'],
    queryFn: async () =>
      (await apiClient.get<{ id: string; plant_name: string }[]>('/plants')).data,
  })

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['qc-pending-report', plantId, overdue],
    queryFn: () => cottonRmApiPartC.qcPending(
      { plant_id: plantId, overdue_days: overdue }),
  })

  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>QC Pending Report</Title>
      <Text type="secondary">
        Every GRN that still has open quality work — moisture samples
        not entered, HVI not read, bales on hold, rejected bales waiting
        for supplier-return, and overdue GRNs.
      </Text>

      <Space wrap style={{ marginTop: 12, marginBottom: 8 }}>
        <Select placeholder="All plants" allowClear style={{ width: 220 }}
                value={plantId} onChange={setPlantId}
                options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
        <Space size={4}>
          <Text>Overdue threshold:</Text>
          <InputNumber min={1} max={30} value={overdue}
                       onChange={v => setOverdue((v as number) || 3)}
                       addonAfter="days" style={{ width: 130 }} />
        </Space>
        <Button icon={<ReloadOutlined spin={isFetching} />}
                onClick={() => refetch()}>Refresh</Button>
      </Space>

      <Card>
        {isLoading || !data ? <Skeleton active /> : (
          <Tabs activeKey={tab} onChange={k => setTab(k as TabKey)}
                items={TAB_DEFS.map(t => ({
                  key: t.key,
                  label: (
                    <Space size={4}>
                      <span>{t.label}</span>
                      <Badge count={data.totals[t.key] ?? 0}
                              showZero={false}
                              style={{ backgroundColor:
                                t.key === 'overdue' || t.key === 'qc_hold' || t.key === 'rejected_return'
                                  ? '#ff4d4f' : '#faad14' }} />
                    </Space>
                  ),
                  children: <GrnTable tab={t.key} rows={data[t.key]} />,
                }))} />
        )}
      </Card>

      {data && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Report generated at {new Date(data.as_of).toLocaleString()} —
          overdue threshold = {data.overdue_days_threshold} days.
        </Text>
      )}
    </div>
  )
}
